import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Stack, Paper, Tabs, Tab, Table, TableHead, TableRow, TableCell, Chip, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Snackbar, Tooltip, Divider,
  InputAdornment, Card, CardContent, CardActions, LinearProgress,
  Autocomplete, Grid,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import DrawIcon from '@mui/icons-material/Draw';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import PendingIcon from '@mui/icons-material/Pending';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PrintIcon from '@mui/icons-material/Print';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useMutation } from '@apollo/client';
import {
  CONTRACTS_QUERY, CONTRACT_TEMPLATES_QUERY, USERS_QUERY,
  CREATE_CONTRACT_MUTATION, UPDATE_CONTRACT_MUTATION, DELETE_CONTRACT_MUTATION,
  SEND_CONTRACT_FOR_REVIEW_MUTATION, ADMIN_SIGN_CONTRACT_MUTATION, REJECT_VENDOR_SERVICE_CONTRACT_MUTATION,
  CREATE_CONTRACT_TEMPLATE_MUTATION, UPDATE_CONTRACT_TEMPLATE_MUTATION, DELETE_CONTRACT_TEMPLATE_MUTATION,
  WEDDING_SERVICES_QUERY, CONTRACT_SERVICE_ITEMS_QUERY, UPDATE_CONTRACT_SERVICE_ITEM_MUTATION,
  ADD_CONTRACT_SERVICE_ITEM_MUTATION, REMOVE_CONTRACT_SERVICE_ITEM_MUTATION,
  APPROVE_CONTRACT_SERVICE_ITEM_MUTATION, BULK_ADD_CONTRACT_SERVICE_ITEMS_MUTATION,
  ADD_CONTRACT_MILESTONE_MUTATION, MARK_MILESTONE_PAID_MUTATION, DELETE_CONTRACT_MILESTONE_MUTATION,
  DUPLICATE_CONTRACT_MUTATION, ALL_SERVICE_CATEGORIES_QUERY,
} from '../graphql/queries';
import SignaturePad from '../components/SignaturePad';
import ContractItemDialog, { needsPrice } from '../components/ContractItemDialog';
import { fmtDate } from '../utils/dateUtils';
import ContractPrintView from '../components/ContractPrintView';
import { useCompanyDetails } from '../utils/companyDetails';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_CONFIG = {
  draft:                  { label: 'Draft',               color: 'default'  },
  sent_for_review:        { label: 'Sent for Review',     color: 'warning'  },
  user_requested_changes: { label: 'Changes Requested',   color: 'info'     },
  user_agreed:            { label: 'User Agreed',         color: 'primary'  },
  signed:                 { label: 'Fully Signed',        color: 'success'  },
  terminated:             { label: 'Terminated',          color: 'error'    },
  // legacy
  active:                 { label: 'Active',              color: 'primary'  },
};

// "Priya Sundaram" when the user has a profile, otherwise their email
const personName = (u) => {
  const p = u?.profile;
  const name = p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';
  return name || u?.email || '';
};
// Who a contract is with: the vendor's business name, or the bride / groom names
const partyLines = (c) => {
  const p = c.parties;
  if (!p) return [];
  if (p.type === 'vendor') return [['Vendor', p.vendorName]];
  if (p.type === 'bride' || p.type === 'groom') return [['Bride', p.brideName], ['Groom', p.groomName]].filter(([, n]) => n);
  return [[c.assignedUser?.role?.displayName || 'Party', p.name]];
};

// Roles present in a list of users, in a stable order, with counts
function rolesWithCounts(items, getRole) {
  const map = new Map();
  items.forEach(it => {
    const r = getRole(it);
    if (!r?.name) return;
    const entry = map.get(r.name) || { name: r.name, label: r.displayName || r.name, count: 0 };
    entry.count++;
    map.set(r.name, entry);
  });
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

const TEMPLATE_PLACEHOLDERS = `Available placeholders:
{{bride_name}}  {{groom_name}}  {{family_name}}
{{date}}  {{location}}  {{cost}}  {{matchmaker_name}}`;

const EMPTY_CONTRACT = {
  title: '', body: '', terms: '', penaltyClause: '',
  cost: '', costDescription: '', dueDate: '', status: 'draft',
  templateId: '', assignedToUser: '',
};
const EMPTY_TEMPLATE = { name: '', description: '', body: '', category: '' };

// ── Signature badge ──────────────────────────────────────────────────────────
function SigBadge({ label, signed, signedAt }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {signed ? <CheckCircleIcon color="success" sx={{ fontSize: 15 }} /> : <PendingIcon color="disabled" sx={{ fontSize: 15 }} />}
      <Typography variant="caption" color={signed ? 'success.dark' : 'text.disabled'}>
        {label}: {signed ? (signedAt ? fmtDate(signedAt, 'datetime') : 'Signed') : 'Pending'}
      </Typography>
    </Stack>
  );
}

// ── Service line-items panel (inside View dialog) ─────────────────────────────
function ServiceItemsPanel({ contractId, contractStatus, onCostChanged }) {
  // A fully signed contract's lines are locked (the server refuses changes too)
  const locked = ['signed', 'terminated'].includes(contractStatus);
  const { data, loading, refetch } = useQuery(CONTRACT_SERVICE_ITEMS_QUERY, {
    variables: { contractId },
    fetchPolicy: 'network-only',
  });
  const { data: svcData } = useQuery(WEDDING_SERVICES_QUERY, { variables: { activeOnly: true }, fetchPolicy: 'network-only' });

  // Adding/removing a line item changes the contract's stored cost server-side —
  // refetch the panel's items AND the parent contracts list so the list/summary
  // views don't keep showing the pre-edit total.
  const afterCostChange = () => { refetch(); onCostChanged?.(); };
  const [addItem, { loading: adding }] = useMutation(ADD_CONTRACT_SERVICE_ITEM_MUTATION, { onCompleted: afterCostChange });
  const [updateItem, { loading: updating }] = useMutation(UPDATE_CONTRACT_SERVICE_ITEM_MUTATION, { onCompleted: afterCostChange });
  const [removeItem] = useMutation(REMOVE_CONTRACT_SERVICE_ITEM_MUTATION, { onCompleted: afterCostChange });
  const [approveItem] = useMutation(APPROVE_CONTRACT_SERVICE_ITEM_MUTATION, { onCompleted: refetch });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // line being edited
  const [itemError, setItemError] = useState('');

  const services = svcData?.weddingServices || [];
  const items = data?.contractServiceItems || [];

  const gross   = items.reduce((s, i) => s + Number(i.subtotal),      0);
  const disc    = items.reduce((s, i) => s + Number(i.discountAmount), 0);
  const tax     = items.reduce((s, i) => s + Number(i.taxAmount),      0);
  const net     = items.reduce((s, i) => s + Number(i.lineTotal),      0);

  const saveItem = async (values) => {
    setItemError('');
    try {
      if (editing) await updateItem({ variables: { input: { id: editing.id, ...values } } });
      else await addItem({ variables: { input: { contractId, ...values, addedBy: 'admin' } } });
      setPickerOpen(false);
      setEditing(null);
    } catch (e) { setItemError(e.message); }
  };

  function fmtINR(n) {
    return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ShoppingCartIcon fontSize="small" /> Service Line Items
        </Typography>
        {locked
          ? <Typography variant="caption" color="text.secondary">Signed: pricing is locked</Typography>
          : (
            <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => { setItemError(''); setPickerOpen(true); }}>
              Add Service
            </Button>
          )}
      </Stack>

      {loading && <CircularProgress size={20} sx={{ display: 'block', mx: 'auto' }} />}

      {!locked && items.some(needsPrice) && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {items.filter(needsPrice).length} line(s) still need a unit price. Quantity, unit price, discount and tax are required
          before this contract can be sent for review or signed.
        </Alert>
      )}

      {items.length > 0 && (
        <>
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Discount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Tax</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Line Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.serviceName}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.serviceCategory} · {item.unit}</Typography>
                    {!locked && needsPrice(item) && <Chip label="Price needed" size="small" color="error" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                  </TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{fmtINR(item.unitPrice)}</TableCell>
                  <TableCell align="right">
                    {Number(item.discountAmount) > 0
                      ? <Typography variant="body2" color="success.main">
                          -{fmtINR(item.discountAmount)}
                          <Typography component="span" variant="caption" color="text.secondary" display="block">
                            {item.discountType === 'percent' ? `${Number(item.discountValue)}%` : 'flat'}
                          </Typography>
                        </Typography>
                      : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    {fmtINR(item.taxAmount)}
                    <Typography variant="caption" color="text.secondary" display="block">{Number(item.taxRate)}%</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}>{fmtINR(item.lineTotal)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {item.isApproved ? (
                      <Chip label="Approved" color="success" size="small" />
                    ) : (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Chip label={item.addedBy === 'user' ? 'User Added' : 'Pending'} size="small"
                          color={item.addedBy === 'user' ? 'info' : 'default'} />
                        {item.addedBy === 'user' && (
                          <Tooltip title="Approve this item">
                            <IconButton size="small" color="success"
                              onClick={() => approveItem({ variables: { id: item.id } })}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {!locked && (
                      <>
                        <Tooltip title="Edit quantity, price, discount and tax">
                          <IconButton size="small" onClick={() => { setItemError(''); setEditing(item); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error"
                            onClick={() => removeItem({ variables: { id: item.id } })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pricing summary */}
          <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Gross Amount</Typography><Typography variant="body2" fontWeight={600}>{fmtINR(gross)}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Total Discount</Typography><Typography variant="body2" fontWeight={600} color="success.main">-{fmtINR(disc)}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">GST / Tax</Typography><Typography variant="body2" fontWeight={600} color="warning.main">+{fmtINR(tax)}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" fontWeight={700}>Net Payable</Typography><Typography variant="h6" fontWeight={800} color="primary.main">{fmtINR(net)}</Typography></Grid>
            </Grid>
          </Paper>
        </>
      )}

      {!loading && items.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>No service items yet. Click "Add Service" to build the pricing.</Alert>
      )}

      <ContractItemDialog
        open={pickerOpen || Boolean(editing)}
        item={editing}
        services={services}
        canPrice
        saving={adding || updating}
        error={itemError}
        onClose={() => { setPickerOpen(false); setEditing(null); }}
        onSave={saveItem}
      />
    </Box>
  );
}

// ── Admin Milestones panel (full CRUD for admin) ──────────────────────────────
function AdminMilestonesPanel({ contractId, milestones: initialMilestones, onRefetch }) {
  const [milestones, setMilestones] = useState(initialMilestones || []);
  const [addOpen, setAddOpen]       = useState(false);
  const [form, setForm]             = useState({ stageName: '', amount: '', dueDate: '', notes: '' });

  useEffect(() => { setMilestones(initialMilestones || []); }, [initialMilestones]);

  const [addMilestone, { loading: adding }] = useMutation(ADD_CONTRACT_MILESTONE_MUTATION, {
    onCompleted: (data) => {
      setMilestones(prev => [...prev, data.addContractMilestone]);
      setAddOpen(false);
      setForm({ stageName: '', amount: '', dueDate: '', notes: '' });
      onRefetch();
    },
  });
  const [markPaid]   = useMutation(MARK_MILESTONE_PAID_MUTATION, {
    onCompleted: (data) => {
      setMilestones(prev => prev.map(m => m.id === data.markMilestonePaid.id ? data.markMilestonePaid : m));
      onRefetch();
    },
  });
  const [deleteMile] = useMutation(DELETE_CONTRACT_MILESTONE_MUTATION, {
    onCompleted: (_, { variables }) => {
      setMilestones(prev => prev.filter(m => m.id !== variables.id));
      onRefetch();
    },
  });

  const totalAmount = milestones.reduce((s, m) => s + Number(m.amount), 0);
  const paidAmount  = milestones.filter(m => m.paidAt).reduce((s, m) => s + Number(m.amount), 0);
  const paidPct     = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

  const handleAdd = () => {
    if (!form.stageName.trim() || !form.amount) return;
    addMilestone({ variables: {
      contractId,
      stageName: form.stageName.trim(),
      amount:    parseFloat(form.amount),
      dueDate:   form.dueDate || null,
      notes:     form.notes   || null,
    }});
  };

  function fmtINR(n) { return `₹${Number(n || 0).toLocaleString('en-IN')}`; }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>Payment Milestones</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add Milestone</Button>
      </Stack>

      {milestones.length > 0 && (
        <Box mb={1.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Paid: {fmtINR(paidAmount)} / {fmtINR(totalAmount)}
            </Typography>
            <Typography variant="caption" fontWeight={600}>{paidPct}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={paidPct}
            color={paidPct === 100 ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
        </Box>
      )}

      {milestones.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No payment milestones yet. Add stages like "30% Upfront", "40% Ceremony Day", "30% Post-Event".
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 0.5, width: 32 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Stage Name</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 0.5 }} align="right">Amount</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Paid At</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Status</TableCell>
              <TableCell sx={{ py: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {milestones.map((m, idx) => (
              <TableRow key={m.id} sx={{ bgcolor: m.paidAt ? '#f1f8e9' : 'transparent' }}>
                <TableCell sx={{ py: 0.5, color: 'text.secondary' }}>{idx + 1}</TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2">{m.stageName}</Typography>
                  {m.notes && <Typography variant="caption" color="text.secondary" display="block">{m.notes}</Typography>}
                </TableCell>
                <TableCell sx={{ py: 0.5 }} align="right">
                  <Typography variant="body2" fontWeight={600}>{fmtINR(m.amount)}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{m.dueDate ? fmtDate(m.dueDate) : '\u2014'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" color={m.paidAt ? 'success.main' : 'text.disabled'}>
                    {m.paidAt ? fmtDate(m.paidAt) : '\u2014'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  {m.paidAt
                    ? <Chip label={`Paid ${fmtDate(m.paidAt)}`} color="success" size="small" icon={<CheckCircleIcon />} />
                    : <Chip label="Pending" color="default" size="small" />}
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Stack direction="row">
                    {!m.paidAt && (
                      <Tooltip title="Mark as Paid">
                        <IconButton size="small" color="success" onClick={() => markPaid({ variables: { id: m.id } })}>
                          <PaymentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete milestone">
                      <IconButton size="small" color="error" onClick={() => deleteMile({ variables: { id: m.id } })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {addOpen && (
        <Paper variant="outlined" sx={{ p: 2, mt: 1, borderRadius: 2 }}>
          <Typography variant="caption" fontWeight={700} color="primary" display="block" mb={1}>New Milestone</Typography>
          <Stack spacing={1.5}>
            <TextField label="Stage Name" size="small" value={form.stageName}
              onChange={e => setForm(f => ({ ...f, stageName: e.target.value }))}
              placeholder="e.g. 30% Upfront Payment" fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label="Amount (₹)" size="small" type="number" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                sx={{ flex: 1 }} />
              <TextField label="Due Date" size="small" type="date" value={form.dueDate}
                onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            </Stack>
            <TextField label="Notes (optional)" size="small" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button size="small" variant="contained" disabled={adding || !form.stageName.trim() || !form.amount}
                onClick={handleAdd}>
                {adding ? <CircularProgress size={14} /> : 'Add'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

function ContractDialog({ open, onClose, users, templates, initial, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const [assignRole, setAssignRole] = useState('');
  useEffect(() => { setForm(initial); setAssignRole(''); }, [initial]);
  const activeUsers = useMemo(() => users.filter(u => u.isActive !== false), [users]);
  const assignRoles = useMemo(() => rolesWithCounts(activeUsers, u => u.role), [activeUsers]);
  const assignOptions = useMemo(
    () => activeUsers
      .filter(u => !assignRole || u.role?.name === assignRole)
      .sort((a, b) => (a.role?.displayName || '').localeCompare(b.role?.displayName || '') || personName(a).localeCompare(personName(b))),
    [activeUsers, assignRole],
  );
  const assignedUser = users.find(u => u.id === form.assignedToUser) || null;
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleTemplateSelect = (tmplId) => {
    const tmpl = templates.find(t => t.id === tmplId);
    setForm(p => ({ ...p, templateId: tmplId, body: tmpl ? tmpl.body : p.body }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initial.id ? 'Edit Contract' : 'New Contract'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {initial.id && initial.status !== 'draft' && (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              This contract has already left draft. Saving changes here will reset it to{' '}
              <strong>{form.assignedToUser ? 'Sent for Review' : 'Draft'}</strong> and clear any signatures collected
              so far — it will need to go through the review/agree/sign process again.
            </Alert>
          )}
          <TextField label="Title" value={form.title} onChange={f('title')} size="small" fullWidth required />
          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Template (optional)</InputLabel>
              <Select value={form.templateId} label="Template (optional)"
                onChange={e => handleTemplateSelect(e.target.value)}>
                <MenuItem value=""><em>None — start from scratch</em></MenuItem>
                {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name} {t.category ? `(${t.category})` : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Autocomplete
                size="small"
                options={assignOptions}
                value={assignedUser}
                onChange={(_, u) => setForm(p => ({ ...p, assignedToUser: u?.id || '' }))}
                groupBy={u => u.role?.displayName || 'No role'}
                getOptionLabel={u => `${personName(u)}${personName(u) !== u.email ? ` (${u.email})` : ''}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, u) => (
                  <li {...props} key={u.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{personName(u)}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="No users match"
                renderInput={params => (
                  <TextField {...params} label="Assign to user" placeholder="Search by name or email"
                    helperText={assignedUser ? `Role: ${assignedUser.role?.displayName || '—'}` : 'Leave empty to keep the contract unassigned'} />
                )}
              />
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" label="All roles" color={!assignRole ? 'primary' : 'default'} variant={!assignRole ? 'filled' : 'outlined'} onClick={() => setAssignRole('')} />
                {assignRoles.map(r => (
                  <Chip key={r.name} size="small" label={`${r.label} (${r.count})`}
                    color={assignRole === r.name ? 'primary' : 'default'} variant={assignRole === r.name ? 'filled' : 'outlined'}
                    onClick={() => setAssignRole(r.name)} />
                ))}
              </Stack>
            </Stack>
          </Stack>
          <TextField label="Contract Body / Terms" value={form.body} onChange={f('body')}
            size="small" fullWidth multiline rows={7}
            placeholder="Enter full contract content. Use placeholders like {{bride_name}}, {{date}} etc." />
          <TextField label="Penalty Clause" value={form.penaltyClause} onChange={f('penaltyClause')}
            size="small" fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Cost (₹)" value={form.cost} onChange={f('cost')} size="small" type="number" fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            <TextField label="Due Date" value={form.dueDate} onChange={f('dueDate')}
              size="small" type="date" fullWidth InputLabelProps={{ shrink: true }} />
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} label="Status" onChange={f('status')}>
                {Object.keys(STATUS_CONFIG).map(s => (
                  <MenuItem key={s} value={s}>{STATUS_CONFIG[s].label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField label="Cost Description" value={form.costDescription} onChange={f('costDescription')}
            size="small" fullWidth placeholder="e.g. Matchmaking fee + documentation charges" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TemplateDialog({ open, onClose, initial, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initial.id ? 'Edit Template' : 'New Template'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={2}>
            <TextField label="Template Name" value={form.name} onChange={f('name')} size="small" fullWidth required />
            <TextField label="Category" value={form.category} onChange={f('category')} size="small" fullWidth
              placeholder="e.g. matchmaking, venue, catering" />
          </Stack>
          <TextField label="Description" value={form.description} onChange={f('description')}
            size="small" fullWidth multiline rows={2} />
          <TextField label="Template Body" value={form.body} onChange={f('body')}
            size="small" fullWidth multiline rows={10} required
            placeholder={TEMPLATE_PLACEHOLDERS} />
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Use placeholders like <strong>{'{{bride_name}}'}</strong>, <strong>{'{{groom_name}}'}</strong>,{' '}
            <strong>{'{{date}}'}</strong>, <strong>{'{{cost}}'}</strong> — they will be filled when creating a contract.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminContractsPage() {
  const COMPANY = useCompanyDetails();
  const [tab, setTab] = useState(0);

  const { data: cData, loading: cLoading, error: cError, refetch: cRefetch } = useQuery(CONTRACTS_QUERY);
  const { data: tData, loading: tLoading, refetch: tRefetch }                = useQuery(CONTRACT_TEMPLATES_QUERY);
  const { data: uData }                                                       = useQuery(USERS_QUERY);

  const [createContract]       = useMutation(CREATE_CONTRACT_MUTATION);
  const [updateContract]       = useMutation(UPDATE_CONTRACT_MUTATION);
  const [deleteContract]       = useMutation(DELETE_CONTRACT_MUTATION);
  const [duplicateContract]    = useMutation(DUPLICATE_CONTRACT_MUTATION);
  const [sendForReview]        = useMutation(SEND_CONTRACT_FOR_REVIEW_MUTATION);
  const [adminSignContract]    = useMutation(ADMIN_SIGN_CONTRACT_MUTATION);
  const [rejectVendorContract] = useMutation(REJECT_VENDOR_SERVICE_CONTRACT_MUTATION);
  const [createTemplate]       = useMutation(CREATE_CONTRACT_TEMPLATE_MUTATION);
  const [updateTemplate]       = useMutation(UPDATE_CONTRACT_TEMPLATE_MUTATION);
  const [deleteTemplate]       = useMutation(DELETE_CONTRACT_TEMPLATE_MUTATION);

  const [contractDialog, setContractDialog] = useState({ open: false, item: EMPTY_CONTRACT });
  const [templateDialog, setTemplateDialog] = useState({ open: false, item: EMPTY_TEMPLATE });
  const [viewDialog, setViewDialog]         = useState({ open: false, contract: null });
  const [printDialog, setPrintDialog]       = useState({ open: false, contract: null });
  const [adminSignDialog, setAdminSignDialog] = useState({ open: false, contractId: null, title: '' });
  const [confirmDel, setConfirmDel]         = useState(null);
  const [rejectDialog, setRejectDialog]     = useState({ open: false, contract: null, reason: '' });
  const [adminCanvasData, setAdminCanvasData] = useState(null);
  const adminSigPadRef = useRef(null);
  const [saving, setSaving]                 = useState(false);
  const [snack, setSnack]                   = useState({ open: false, msg: '', severity: 'success' });

  const contracts  = cData?.contracts       || [];
  const [filters, setFilters] = useState({ search: '', role: '', status: '', service: '' });
  // Services on the contracts, for the Service filter and for searching by service
  const { data: catData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: false } });
  const catLabel = useMemo(() => Object.fromEntries((catData?.allServiceCategories || []).map(c => [c.name, c.displayName])), [catData]);
  const contractServices = useMemo(() => {
    const cats = new Set(contracts.flatMap(c => (c.serviceItems || []).map(i => i.serviceCategory).filter(Boolean)));
    return [...cats].map(name => ({ name, label: catLabel[name] || name })).sort((a, b) => a.label.localeCompare(b.label));
  }, [contracts, catLabel]);
  const contractRoles = useMemo(() => rolesWithCounts(contracts, c => c.assignedUser?.role), [contracts]);
  const unassignedCount = contracts.filter(c => !c.assignedUser).length;
  const filteredContracts = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return contracts.filter(c => {
      if (filters.role === 'unassigned' ? c.assignedUser : filters.role && c.assignedUser?.role?.name !== filters.role) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (filters.service && !(c.serviceItems || []).some(i => i.serviceCategory === filters.service)) return false;
      if (!q) return true;
      return [c.refNumber, c.title, c.assignedUser?.email, personName(c.assignedUser), c.assignedUser?.role?.displayName,
        c.parties?.vendorName, c.parties?.brideName, c.parties?.groomName,
        ...(c.serviceItems || []).flatMap(i => [i.serviceName, catLabel[i.serviceCategory] || i.serviceCategory])]
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [contracts, filters, catLabel]);
  const filtersActive = Boolean(filters.search || filters.role || filters.status || filters.service);
  const templates  = tData?.contractTemplates || [];
  const users      = uData?.users           || [];
  const showSnack  = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // Refetch the contracts list and, if the view dialog is open on this same
  // contract, refresh its snapshot too — otherwise the cost shown at the top of
  // the dialog stays stuck at its pre-edit value while items are edited below it.
  const refreshContractCost = async (contractId) => {
    const result = await cRefetch();
    const updated = result?.data?.contracts?.find(c => c.id === contractId);
    if (updated) {
      setViewDialog(v => (v.open && v.contract?.id === contractId ? { ...v, contract: updated } : v));
    }
  };

  const handleSaveContract = async (form) => {
    setSaving(true);
    try {
      const input = {
        title: form.title, body: form.body || null, terms: form.terms || '',
        penaltyClause: form.penaltyClause || null,
        templateId: form.templateId || null, assignedToUser: form.assignedToUser || null,
        cost: form.cost ? parseFloat(form.cost) : null, costDescription: form.costDescription || null,
        dueDate: form.dueDate || null, status: form.status,
      };
      if (form.id) {
        await updateContract({ variables: { input: { ...input, id: form.id } } });
        showSnack('Contract updated');
      } else {
        await createContract({ variables: { input } });
        showSnack('Contract created');
      }
      await cRefetch();
      setContractDialog({ open: false, item: EMPTY_CONTRACT });
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleSaveTemplate = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await updateTemplate({ variables: { input: { id: form.id, name: form.name, description: form.description, body: form.body, category: form.category } } });
        showSnack('Template updated');
      } else {
        await createTemplate({ variables: { input: { name: form.name, description: form.description, body: form.body, category: form.category } } });
        showSnack('Template created');
      }
      await tRefetch();
      setTemplateDialog({ open: false, item: EMPTY_TEMPLATE });
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteContract = async (id) => {
    setSaving(true);
    try {
      await deleteContract({ variables: { id } });
      await cRefetch();
      setConfirmDel(null);
      showSnack('Contract deleted');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDuplicateContract = async (id) => {
    setSaving(true);
    try {
      await duplicateContract({ variables: { id } });
      await cRefetch();
      showSnack('Contract duplicated as draft');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteTemplate = async (id) => {
    setSaving(true);
    try {
      await deleteTemplate({ variables: { id } });
      await tRefetch();
      setConfirmDel(null);
      showSnack('Template deleted');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleSendForReview = async (id) => {
    setSaving(true);
    try {
      await sendForReview({ variables: { id } });
      await cRefetch();
      showSnack('Contract sent for user review');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleAdminSign = async () => {
    if (!adminCanvasData) return;
    setSaving(true);
    try {
      await adminSignContract({ variables: { id: adminSignDialog.contractId, signatureData: adminCanvasData } });
      await cRefetch();
      setAdminSignDialog({ open: false, contractId: null, title: '' });
      adminSigPadRef.current?.clear();
      setAdminCanvasData(null);
      showSnack('Admin signature recorded! Contract fully signed once user also signs.');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleRejectVendorContract = async () => {
    setSaving(true);
    try {
      await rejectVendorContract({ variables: { id: rejectDialog.contract.id, reason: rejectDialog.reason } });
      await cRefetch();
      setRejectDialog({ open: false, contract: null, reason: '' });
      showSnack('Vendor contract rejected; the vendor has been notified');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (cLoading || tLoading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (cError) return <Alert severity="error" sx={{ m: 3 }}>{cError.message}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <DescriptionIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Contracts</Typography>
            <Typography variant="body2" color="text.secondary">
              Create contracts, send for review, and finalize with dual e-signatures
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setContractDialog({ open: true, item: { ...EMPTY_CONTRACT } })}>
          New Contract
        </Button>
      </Stack>

      {/* ── Company Details ── */}
      <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#1a237e', px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} color="white" letterSpacing={1}>{COMPANY.name} — Company Details</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell>Address</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>GSTIN</TableCell>
              <TableCell>CIN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{COMPANY.address}</TableCell>
              <TableCell>{COMPANY.phone}</TableCell>
              <TableCell>{COMPANY.email}</TableCell>
              <TableCell>{COMPANY.website}</TableCell>
              <TableCell>{COMPANY.gstin}</TableCell>
              <TableCell>{COMPANY.cin}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </Box>
      </Paper>

      {/* Workflow legend */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 3, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <LockIcon color="success" fontSize="small" />
          <Typography variant="caption" color="success.dark" fontWeight={600}>
            Workflow: Draft, Send for Review, User Agrees / Requests Changes, Both E-Sign, Fully Executed
          </Typography>
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Contracts (${contracts.length})`} icon={<DescriptionIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label={`Templates (${templates.length})`} icon={<ArticleIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* ── Contracts Tab ── */}
        {tab === 0 && (
          <Box>
            <Stack spacing={1.5} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  size="small" sx={{ flex: 1 }} value={filters.search}
                  placeholder="Search by bride/groom or vendor name, email, service, title or ref #"
                  onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
                <TextField select size="small" label="Status" value={filters.status} sx={{ minWidth: 200 }}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="">All statuses</MenuItem>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Service" value={filters.service} sx={{ minWidth: 200 }}
                  onChange={e => setFilters(f => ({ ...f, service: e.target.value }))}>
                  <MenuItem value="">All services</MenuItem>
                  {contractServices.map(s => <MenuItem key={s.name} value={s.name}>{s.label}</MenuItem>)}
                </TextField>
                {filtersActive && <Button onClick={() => setFilters({ search: '', role: '', status: '', service: '' })}>Clear</Button>}
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Assigned to:</Typography>
                <Chip size="small" label={`All (${contracts.length})`} color={!filters.role ? 'primary' : 'default'}
                  variant={!filters.role ? 'filled' : 'outlined'} onClick={() => setFilters(f => ({ ...f, role: '' }))} />
                {contractRoles.map(r => (
                  <Chip key={r.name} size="small" label={`${r.label} (${r.count})`}
                    color={filters.role === r.name ? 'primary' : 'default'} variant={filters.role === r.name ? 'filled' : 'outlined'}
                    onClick={() => setFilters(f => ({ ...f, role: r.name }))} />
                ))}
                {unassignedCount > 0 && (
                  <Chip size="small" label={`Unassigned (${unassignedCount})`}
                    color={filters.role === 'unassigned' ? 'primary' : 'default'} variant={filters.role === 'unassigned' ? 'filled' : 'outlined'}
                    onClick={() => setFilters(f => ({ ...f, role: 'unassigned' }))} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Showing {filteredContracts.length} of {contracts.length}
                </Typography>
              </Stack>
            </Stack>
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell>Ref #</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Signatures</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContracts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {contracts.length ? 'No contracts match these filters.' : 'No contracts yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {filteredContracts.map(c => {
                  const cfg = STATUS_CONFIG[c.status] || { label: c.status, color: 'default' };
                  const canSend  = ['draft', 'active', 'user_requested_changes'].includes(c.status) && c.assignedToUser;
                  const canAdminSign = c.status === 'user_agreed' && !c.signatureData;
                  // Vendor-initiated service contracts: the vendor created it for themselves
                  const isVendorRequest = Boolean(c.creator?.id) && c.creator.id === c.assignedToUser;
                  const canReject = isVendorRequest && !['signed', 'terminated'].includes(c.status);
                  const signPct  = c.status === 'user_agreed' || c.status === 'signed'
                    ? ((c.signatureData ? 1 : 0) + (c.userSignatureData ? 1 : 0)) * 50 : 0;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {c.refNumber || '\u2014'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{c.title}</Typography>
                        {(c.serviceItems || []).length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[...new Set(c.serviceItems.map(i => catLabel[i.serviceCategory] || i.serviceCategory).filter(Boolean))].join(' · ')}
                          </Typography>
                        )}
                        {isVendorRequest && (
                          <Chip label="Vendor request" size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: 11, mt: 0.3 }} />
                        )}
                        {c.reviewNotes && (
                          <Typography variant="caption" color="info.dark" display="block">
                            Change request: {c.reviewNotes.substring(0, 60)}{c.reviewNotes.length > 60 ? '\u2026' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {c.assignedUser ? (
                          <Box>
                            {partyLines(c).length ? partyLines(c).map(([k, n]) => (
                              <Typography key={k} variant="body2"><Box component="span" color="text.secondary">{k}: </Box><b>{n}</b></Typography>
                            )) : <Typography variant="body2" fontWeight={600}>{personName(c.assignedUser)}</Typography>}
                            {(c.parties?.name || personName(c.assignedUser)) !== c.assignedUser.email && (
                              <Typography variant="caption" color="text.secondary" display="block">{c.assignedUser.email}</Typography>
                            )}
                            {c.assignedUser.role && (
                              <Chip size="small" variant="outlined" label={c.assignedUser.role.displayName} sx={{ height: 18, fontSize: 11, mt: 0.3 }} />
                            )}
                          </Box>
                        ) : <em style={{ color: '#bbb' }}>Unassigned</em>}
                      </TableCell>
                      <TableCell>{c.cost ? `₹${Number(c.cost).toLocaleString()}` : '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{fmtDate(c.dueDate)}</TableCell>
                      <TableCell>
                        <Chip label={cfg.label} size="small" color={cfg.color} />
                        {/* Final step: the family/vendor accepts the approved contract */}
                        {c.status === 'signed' && (
                          <Chip
                            size="small" variant="outlined" sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }}
                            color={c.acceptedAt ? 'success' : 'warning'}
                            label={c.acceptedAt ? `Accepted ${fmtDate(c.acceptedAt)}` : 'Awaiting acceptance'}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <Stack spacing={0.3}>
                          <SigBadge label="Admin" signed={Boolean(c.signatureData)} signedAt={c.signedAt} />
                          <SigBadge label="User"  signed={Boolean(c.userSignatureData)} signedAt={c.userSignedAt} />
                        </Stack>
                        {signPct > 0 && (
                          <LinearProgress variant="determinate" value={signPct}
                            color={signPct === 100 ? 'success' : 'primary'}
                            sx={{ height: 4, borderRadius: 2, mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setViewDialog({ open: true, contract: c })}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print / Save as PDF">
                          <IconButton size="small" color="secondary" onClick={() => setPrintDialog({ open: true, contract: c })}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canSend && (
                          <Tooltip title="Send for user review">
                            <IconButton size="small" color="warning" disabled={saving}
                              onClick={() => handleSendForReview(c.id)}>
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAdminSign && (
                          <Tooltip title={isVendorRequest ? 'Approve & sign vendor contract' : 'Admin sign contract'}>
                            <IconButton size="small" color="success"
                              onClick={() => setAdminSignDialog({ open: true, contractId: c.id, title: c.title })}>
                              <HistoryEduIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canReject && (
                          <Tooltip title="Reject vendor request">
                            <IconButton size="small" color="error"
                              onClick={() => setRejectDialog({ open: true, contract: c, reason: '' })}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={c.status === 'draft' ? 'Edit' : 'Edit (will reset for re-approval)'}>
                          <IconButton size="small" color="primary"
                            onClick={() => setContractDialog({ open: true, item: {
                              id: c.id, title: c.title, body: c.body || '', terms: c.terms || '',
                              penaltyClause: c.penaltyClause || '', cost: c.cost || '',
                              costDescription: c.costDescription || '', dueDate: c.dueDate || '',
                              status: c.status, templateId: c.templateId || '',
                              assignedToUser: c.assignedToUser || '',
                            }})}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton size="small" color="default" disabled={saving}
                            onClick={() => handleDuplicateContract(c.id)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error"
                            onClick={() => setConfirmDel({ id: c.id, type: 'contract', label: c.title })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {contracts.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>No contracts yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            </Box>
          </Box>
        )}

        {/* ── Templates Tab ── */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              <Button variant="outlined" startIcon={<AddIcon />}
                onClick={() => setTemplateDialog({ open: true, item: { ...EMPTY_TEMPLATE } })}>
                New Template
              </Button>
            </Stack>
            <Stack spacing={2}>
              <Paged items={templates}>{rows => rows.map(t => (
                <Card key={t.id} elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6" fontWeight={700}>{t.name}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mt={0.3}>
                          {t.category && <Chip label={t.category} size="small" color="primary" variant="outlined" />}
                          <Chip label={t.isActive ? 'Active' : 'Inactive'} size="small" color={t.isActive ? 'success' : 'default'} />
                        </Stack>
                        {t.description && <Typography variant="body2" color="text.secondary" mt={0.8}>{t.description}</Typography>}
                      </Box>
                    </Stack>
                    <Divider sx={{ mt: 1.5, mb: 1 }} />
                    <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, maxHeight: 120, overflow: 'hidden' }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {t.body?.substring(0, 400)}{t.body?.length > 400 ? '…' : ''}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 1.5 }}>
                    <Button size="small" startIcon={<AddIcon />}
                      onClick={() => setContractDialog({ open: true, item: { ...EMPTY_CONTRACT, templateId: t.id, body: t.body } })}>
                      Use Template
                    </Button>
                    <Box flex={1} />
                    <IconButton size="small" color="primary"
                      onClick={() => setTemplateDialog({ open: true, item: { id: t.id, name: t.name, description: t.description || '', body: t.body, category: t.category || '' } })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error"
                      onClick={() => setConfirmDel({ id: t.id, type: 'template', label: t.name })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              ))}</Paged>
              {templates.length === 0 && (
                <Alert severity="info">No templates yet. Create one to speed up contract creation.</Alert>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Contract Create/Edit dialog */}
      <ContractDialog
        open={contractDialog.open}
        onClose={() => setContractDialog({ open: false, item: EMPTY_CONTRACT })}
        initial={contractDialog.item}
        users={users}
        templates={templates}
        onSave={handleSaveContract}
        saving={saving}
      />

      {/* Template dialog */}
      <TemplateDialog
        open={templateDialog.open}
        onClose={() => setTemplateDialog({ open: false, item: EMPTY_TEMPLATE })}
        initial={templateDialog.item}
        onSave={handleSaveTemplate}
        saving={saving}
      />

      {/* View Contract dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, contract: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700}>{viewDialog.contract?.title}</Typography>
              {viewDialog.contract?.refNumber && (
                <Typography variant="caption" color="text.secondary">Ref: {viewDialog.contract.refNumber}</Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {viewDialog.contract && (
                <Chip label={(STATUS_CONFIG[viewDialog.contract.status] || { label: viewDialog.contract.status }).label}
                  size="small" color={(STATUS_CONFIG[viewDialog.contract.status] || { color: 'default' }).color} />
              )}
              <Tooltip title="Print / Save as PDF">
                <IconButton size="small" onClick={() => setPrintDialog({ open: true, contract: viewDialog.contract })}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {viewDialog.contract && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>REF #</Typography>
                  <Typography variant="body2">{viewDialog.contract.refNumber || '\u2014'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>ASSIGNED TO</Typography>
                  {partyLines(viewDialog.contract).map(([k, n]) => <Typography key={k} variant="body2">{k}: <b>{n}</b></Typography>)}
                  <Typography variant="body2" color={partyLines(viewDialog.contract).length ? 'text.secondary' : undefined}>{viewDialog.contract.assignedUser?.email || 'Unassigned'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>COST</Typography>
                  <Typography variant="body2">{viewDialog.contract.cost ? ` ${Number(viewDialog.contract.cost).toLocaleString('en-IN')}` : '\u2014'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>DUE DATE</Typography>
                  <Typography variant="body2">{fmtDate(viewDialog.contract.dueDate)}</Typography>
                </Box>
              </Stack>
              {viewDialog.contract.reviewNotes && (
                <Alert severity="info" icon={<EditIcon />}>
                  <Typography variant="subtitle2">User's Change Request:</Typography>
                  <Typography variant="body2">{viewDialog.contract.reviewNotes}</Typography>
                </Alert>
              )}
              {viewDialog.contract.costDescription && (
                <Box sx={{ p: 1.5, bgcolor: '#fff8e1', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="warning.dark">Cost Details: </Typography>
                  <Typography variant="body2">{viewDialog.contract.costDescription}</Typography>
                </Box>
              )}
              <Divider />
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, maxHeight: 250, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {viewDialog.contract.body || viewDialog.contract.terms || 'No content'}
                </Typography>
              </Box>
              {viewDialog.contract.penaltyClause && (
                <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="error.dark">Penalty Clause: </Typography>
                  <Typography variant="body2">{viewDialog.contract.penaltyClause}</Typography>
                </Box>
              )}
              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>E-Signature Status</Typography>
              <Stack direction="row" spacing={4}>
                <SigBadge label="Admin (You)" signed={Boolean(viewDialog.contract.signatureData)} signedAt={viewDialog.contract.signedAt} />
                <SigBadge label="User" signed={Boolean(viewDialog.contract.userSignatureData)} signedAt={viewDialog.contract.userSignedAt} />
              </Stack>
              <Divider />
              <ServiceItemsPanel contractId={viewDialog.contract.id} contractStatus={viewDialog.contract.status}
                onCostChanged={() => refreshContractCost(viewDialog.contract.id)} />
              <Divider sx={{ my: 1 }} />
              <AdminMilestonesPanel
                contractId={viewDialog.contract.id}
                milestones={viewDialog.contract.milestones || []}
                onRefetch={cRefetch}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, contract: null })}>Close</Button>
          {viewDialog.contract?.status === 'user_agreed' && !viewDialog.contract?.signatureData && (
            <Button variant="contained" startIcon={<HistoryEduIcon />} color="success"
              onClick={() => {
                setAdminSignDialog({ open: true, contractId: viewDialog.contract.id, title: viewDialog.contract.title });
                setViewDialog({ open: false, contract: null });
              }}>
              Sign as Admin
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Admin sign dialog */}
      <Dialog open={adminSignDialog.open} onClose={() => setAdminSignDialog({ open: false, contractId: null, title: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Admin E-Signature — {adminSignDialog.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              The user has agreed to this contract. Sign to confirm your acceptance and finalize the agreement.
            </Alert>
            <Alert severity="warning">
              This action is irreversible. Both parties will be bound once both have signed.
            </Alert>
            <SignaturePad ref={adminSigPadRef} onChange={setAdminCanvasData} width={460} height={160} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAdminSignDialog({ open: false, contractId: null, title: '' }); adminSigPadRef.current?.clear(); setAdminCanvasData(null); }}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<LockIcon />}
            onClick={handleAdminSign} disabled={saving || !adminCanvasData}>
            {saving ? <CircularProgress size={18} /> : 'Confirm & Sign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)}>
        <DialogTitle>Delete {confirmDel?.type === 'template' ? 'Template' : 'Contract'}?</DialogTitle>
        <DialogContent><Typography>"{confirmDel?.label}" will be permanently removed.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={saving}
            onClick={() => confirmDel?.type === 'template' ? handleDeleteTemplate(confirmDel.id) : handleDeleteContract(confirmDel.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print/Preview dialog */}
      {printDialog.open && printDialog.contract && (
        <ContractPrintView
          open={printDialog.open}
          onClose={() => setPrintDialog({ open: false, contract: null })}
          contract={printDialog.contract}
        />
      )}

      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, contract: null, reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject vendor contract</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            "{rejectDialog.contract?.title}" will be ended and the vendor notified. Their offerings for these services stay hidden.
          </Typography>
          <TextField
            label="Reason (shown to the vendor)" value={rejectDialog.reason} autoFocus fullWidth multiline rows={3}
            onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, contract: null, reason: '' })}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!rejectDialog.reason.trim() || saving} onClick={handleRejectVendorContract}>Reject</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
